import { Button, Chip, Dialog, DialogContent, DialogContentText, DialogTitle, Divider, IconButton, TextField, Tooltip, Typography } from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import { makeStyles } from "@/compat/muiStyles";
import { NotificationsActiveOutlined, NotificationsNoneOutlined } from "@mui/icons-material";
import { Autocomplete } from "@/compat/Autocomplete";
import { validEmailRegex } from "../../utils/RegexUtils";
import { getAcknowledgementStatusDescription } from "../../../constants/acknowledgementStatusConstants";

// AcknowledgementStatus enum: Sent=0, Viewed=1, Acknowledged=2.
const ACKNOWLEDGED = 2;

const useStyles = makeStyles((theme) => ({
    subTitle: {
        padding: theme.spacing(3),
    },
    title: {
        padding: theme.spacing(0),
        margin: theme.spacing(0),
    },
    dialogContent: {
        display: "flex",
        gap: theme.spacing(2),
        alignItems: "flex-start",
        marginBottom: theme.spacing(2),
    },
    error: {
        "&.MuiChip-outlined": {
            border: "1px solid red",
            color: "red",
        },
    },
    inviteButton: {
        marginTop: theme.spacing(1),
    },
    sharedWithHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: theme.spacing(1),
        marginTop: theme.spacing(2),
    },
    emails: {
        display: "flex",
        alignItems: "center",
    },
    chip: {
        marginLeft: "auto",
        fontSize: "0.75rem",
        marginRight: theme.spacing(1),
    },
    emailList: {
        display: "flex",
        flexDirection: "column",
        gap: theme.spacing(1),
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
}));

// A recipient can be either a site contact (picked from the dropdown) or a
// free-typed email string. Both ultimately resolve to an email address.
const toEmail = (item) => (typeof item === "string" ? item.trim() : item?.email);
const contactName = (c) => `${c?.firstName ?? ""} ${c?.lastName ?? ""}`.trim();

// Let the user search the site-user list by name OR email as they type.
const filterContacts = createFilterOptions<any>({
    stringify: (option) => `${option.firstName ?? ""} ${option.lastName ?? ""} ${option.email ?? ""}`,
});

export default function ShareRiskAssessmentDialog({
    emailList,
    shareDialogOpen,
    closeShareDialog,
    riskAssessmentHistoryAcknowledgementRecipients,
    shareRiskAssessmentHistoryInvite,
    remindRecipients,
    setEmailList,
    siteContacts,
}) {
    const classes = useStyles();

    const recipients = riskAssessmentHistoryAcknowledgementRecipients ?? [];
    const recipientEmails = new Set(recipients.map((r) => r.email));
    // Everyone who hasn't acknowledged yet (Sent or Viewed) is "pending".
    const pendingEmails = recipients.filter((r) => r.status !== ACKNOWLEDGED).map((r) => r.email);

    // Site users who have an email address and aren't already recipients.
    const contactOptions = (siteContacts ?? []).filter((c) => c?.email && !recipientEmails.has(c.email));

    const resolvedEmails = emailList.map(toEmail);
    const hasInvalidEmail = emailList.some((item) => typeof item === "string" && !item.trim().match(validEmailRegex));
    const hasExistingRecipient = resolvedEmails.some((email) => recipientEmails.has(email));
    const inviteDisabled = emailList.length === 0 || hasInvalidEmail || hasExistingRecipient;

    return (
        <Dialog open={shareDialogOpen} onClose={closeShareDialog} fullWidth maxWidth="sm">
            <div className={classes.subTitle}>
                <DialogTitle className={classes.title}>Share Risk Assessment</DialogTitle>
                <DialogContentText className={classes.title}>
                    Invite anyone to review this risk assessment. Recipients do not need a SafetyNest account &mdash; add colleagues from the list or type any email address.
                </DialogContentText>
            </div>
            <DialogContent>
                <div className={classes.dialogContent}>
                    <Autocomplete
                        fullWidth
                        multiple
                        freeSolo
                        disableCloseOnSelect
                        filterSelectedOptions
                        id="share-recipients"
                        options={contactOptions}
                        value={emailList}
                        onChange={(e, value) => setEmailList([...value])}
                        filterOptions={filterContacts}
                        getOptionLabel={(option) => (typeof option === "string" ? option : contactName(option) || option.email)}
                        getOptionKey={(option) => (typeof option === "string" ? option : option.externalId)}
                        isOptionEqualToValue={(option, value) => toEmail(option) === toEmail(value)}
                        renderOption={(option) => (
                            <div>
                                <Typography variant="body2">{contactName(option) || option.email}</Typography>
                                {contactName(option) && (
                                    <Typography variant="caption" color="textSecondary">
                                        {option.email}
                                    </Typography>
                                )}
                            </div>
                        )}
                        renderTags={(value, getTagProps) =>
                            value.map((option, index) => {
                                const email = toEmail(option);
                                const label = typeof option === "string" ? option : contactName(option) || option.email;
                                const invalid = (typeof option === "string" && !option.trim().match(validEmailRegex)) || recipientEmails.has(email);
                                const { key, ...tagProps } = getTagProps({ index });
                                return (
                                    <Chip
                                        key={key}
                                        {...tagProps}
                                        classes={invalid ? { outlined: classes.error } : undefined}
                                        variant="outlined"
                                        label={label}
                                        title={email}
                                    />
                                );
                            })
                        }
                        renderInput={(params) => (
                            <TextField {...params} variant="outlined" label="Add people" placeholder="Type a name or email…" />
                        )}
                    />
                    <Button
                        className={classes.inviteButton}
                        variant="contained"
                        color="primary"
                        disabled={inviteDisabled}
                        onClick={() => shareRiskAssessmentHistoryInvite([...new Set(resolvedEmails)], false)}
                    >
                        Invite
                    </Button>
                </div>
                <Divider />
                <div className={classes.sharedWithHeader}>
                    <Typography variant="body2">Shared with:</Typography>
                    {pendingEmails.length > 0 && (
                        <Button
                            size="small"
                            variant="outlined"
                            color="primary"
                            startIcon={<NotificationsActiveOutlined />}
                            onClick={() => remindRecipients(pendingEmails)}
                        >
                            Remind all pending
                        </Button>
                    )}
                </div>
                {recipients.length === 0 && (
                    <Typography color="textSecondary" variant="caption">
                        Add people above to share this risk assessment.
                    </Typography>
                )}
                <div className={classes.emailList}>
                    {recipients.map((x) => (
                        <div className={classes.emails} key={x.id}>
                            <Typography variant="caption">{x.email}</Typography>
                            <Chip className={classes.chip} label={getAcknowledgementStatusDescription(x.status)} variant="outlined" size="small" />
                            {x.status !== ACKNOWLEDGED && (
                                <Tooltip title="Send reminder">
                                    <IconButton size="small" onClick={() => remindRecipients([x.email])}>
                                        <NotificationsNoneOutlined fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        </div>
                    ))}
                </div>
            </DialogContent>
        </Dialog>
    );
}
